import React, { useState, useEffect } from 'react';
import {
  Button,
  Switch,
  FormControlLabel,
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Swal from 'sweetalert2';
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import { sendPasswordResetLink } from '../../services/adminService';
import { setUserPassword } from '../../services/resetPasswordModalService';
import { useTranslation } from 'react-i18next';
const ResetPasswordModal = ({ open, onClose, userId, defaultSendLink = true, labels = {} }) => {
  const { t } = useTranslation();
  const [sendResetLink, setSendResetLink] = useState(defaultSendLink);
  const [pass, setPass] = useState('');
  const [cpass, setCpass] = useState('');
  const [validation, setValidation] = useState({
    minLength: false,
    uppercase: false,
    specialChar: false,
    number: false,
    confirmpass: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!open) {
      setSendResetLink(defaultSendLink);
      setPass('');
      setCpass('');
      setValidation({
        minLength: false,
        uppercase: false,
        specialChar: false,
        number: false,
        confirmpass: false
      });
    }
  }, [open, defaultSendLink]);

  const doConfirm = async () => {
    try {
      setErrorMsg('');
      setSubmitting(true);
      if (!userId) return;

      if (sendResetLink) {
        await sendPasswordResetLink({userId});
        Swal.fire({ icon: 'success', title: labels.sentTitle || 'Link Sent', text: labels.sentText || 'Password reset link sent.' });
      } else {
        if (!(validation.minLength && validation.uppercase && validation.specialChar && validation.number && validation.confirmpass)) {
          setErrorMsg(labels.invalidText || t('GENERAL.PLEASE_MEET_ALL_PASSWORD_REQUIREMENTS'));
          setSubmitting(false);
          return;
        }
        await setUserPassword(userId, pass, cpass);
        Swal.fire({ icon: 'success', title: labels.updatedTitle || 'Password updated' });
      }
      onClose?.();
    } catch (e) {
      setErrorMsg(e.message || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalWrapper
      open={open}
      setOpen={(v) => { if (!v) onClose?.(); }}
      onClose={onClose}
      title={labels.title || t('GENERAL.RESETSET_PASSWORD')}
      content={
        <Box sx={{ p: 0, px: 1 }}>
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 1 }}>{errorMsg}</Alert>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={sendResetLink}
                onChange={(e) => setSendResetLink(e.target.checked)}
                color="primary"
              />
            }
            label={labels.checkbox || t('GENERAL.SEND_PASSWORD_RESET_LINK_TO_USER')}
          />
          {!sendResetLink && (
            <Box sx={{ mt: 1 }}>
              <TextField
                label={labels.newPass || t('LABELS.NEW_PASSWORD')}
                type="password"
                fullWidth
                size="small"
                margin="dense"
                value={pass}
                onChange={(e) => {
                  const p = e.target.value;
                  setPass(p);
                  const minLength = p.length >= 8;
                  const uppercase = /[A-Z]/.test(p);
                  const specialChar = /[!@#$%^&*]/.test(p);
                  const number = /[0-9]/.test(p);
                  setValidation({
                    ...validation,
                    minLength,
                    uppercase,
                    specialChar,
                    number,
                    confirmpass: p === cpass
                  });
                }}
              />
              <TextField
                label={labels.confirmNewPass || t('LABELS.CONFIRM_NEW_PASSWORD')}
                type="password"
                fullWidth
                size="small"
                margin="dense"
                value={cpass}
                onChange={(e) => {
                  const c = e.target.value;
                  setCpass(c);
                  setValidation({ ...validation, confirmpass: pass === c });
                }}
              />
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ display: 'block', color: validation.minLength ? 'green' : 'text.secondary' }}>
                  {validation.minLength ? <CheckCircleIcon fontSize="inherit" /> : <ErrorOutlineIcon fontSize="inherit" />} {t('UI_TEXT.MINIMUM_EIGHT_CHARACTERS')}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: validation.uppercase ? 'green' : 'text.secondary' }}>
                  {validation.uppercase ? <CheckCircleIcon fontSize="inherit" /> : <ErrorOutlineIcon fontSize="inherit" />} {t('UI_TEXT.AT_LEAST_ONE_UPPERCASE')}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: validation.specialChar ? 'green' : 'text.secondary' }}>
                  {validation.specialChar ? <CheckCircleIcon fontSize="inherit" /> : <ErrorOutlineIcon fontSize="inherit" />} {t('UI_TEXT.AT_LEAST_ONE_SPECIAL_CHARACTERS')}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: validation.number ? 'green' : 'text.secondary' }}>
                  {validation.number ? <CheckCircleIcon fontSize="inherit" /> : <ErrorOutlineIcon fontSize="inherit" />} {t('UI_TEXT.AT_LEAST_ONE_NUMBER')}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: validation.confirmpass ? 'green' : 'text.secondary' }}>
                  {validation.confirmpass ? <CheckCircleIcon fontSize="inherit" /> : <ErrorOutlineIcon fontSize="inherit" />} {t('UI_TEXT.CONFIRM_PASSWORD_MATCHES')}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      }
      actions={
        <>
          <Button onClick={onClose}>{labels.cancel || t('BUTTON.CANCEL')}</Button>
          <Button
            variant="contained"
            onClick={doConfirm}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <CircularProgress size={18} sx={{ color: 'white', mr: 1 }} />{' '}
                {labels.confirm || t("TITLES.CONFIRM")}
              </>
            ) : (
              labels.confirm || t("TITLES.CONFIRM")
            )}
          </Button>
        </>
      }
    />
  );
};

export default ResetPasswordModal;
